import React from "react";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import PageHeading from "../UI/PageHeading";
import { Box, Heading, Text, VStack, Badge, LinkBox } from "@chakra-ui/react";
import { Button, LinkOverlay, Image } from "@chakra-ui/react";
import { ExternalLink } from "lucide-react";
import { getFeatureFlagStatus } from "../../featureFlags";

const nftBridgeEnabled = getFeatureFlagStatus("nftBridgeEnabled");
const shadowForkMessagesEnabled = getFeatureFlagStatus(
  "shadowForkMessagesEnabled"
);

const HomePage = () => {
  return (
    <>
      <VStack align="stretch" maxW="full" ml="0">
        <PageHeading title="Welcome to the Palm Network App" />
        <Heading as="h2" fontSize="32px" pl="16px">
          Start here / How to onboard
        </Heading>
        <CTASection
          title="Download and install wallet"
          desc="If you're looking to purchase an NFT or transact in cryptocurrency on Palm, you'll
          need a wallet of some kind. This site supports a number of wallets including MetaMask, a popular wallet that is available as a browser extension for Chrome, Firefox, Brave & Edge, and as a mobile app."
        >
          <LinkBox>
            <LinkOverlay
              href="https://metamask.io"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button h="48px" w="234px">
                <ExternalLink size={16} /> Get MetaMask &nbsp;
                <Image
                  src="metamask-fox.svg"
                  boxSize="25px"
                  alt="MetaMask logo"
                />
              </Button>
            </LinkOverlay>
          </LinkBox>
          &nbsp;&nbsp;
          <LinkBox>
            <LinkOverlay
              href="https://alphawallet.com/download-alphawallet-app/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button h="48px" w="234px">
                <ExternalLink size={16} /> Get AlphaWallet &nbsp;
                <Image
                  src="AlphaWallet-logo.svg"
                  boxSize="25px"
                  alt="MetaMask logo"
                />
              </Button>
            </LinkOverlay>
          </LinkBox>
        </CTASection>
        <Box pl="4px" color="palmNetwork.200" bg="palmNetwork.100">
          <CTASection
            title="Configure your wallet"
            desc={
              shadowForkMessagesEnabled
                ? "The following adds shadow fork Palm Testnet configuration to your wallet."
                : 'Some wallets need to be configured in order to access the Palm network. We make this process easy.  You can add the Palm network to your wallet by using the "Configure Wallet" function below. This also adds the most commonly-used tokens on the Palm network to your wallet.'
            }
          >
            <CTACard
              title="Configure Wallet"
              desc="Add the Palm network and its supported tokens to your wallet."
              badge={false}
              buttonCopy="Get Started"
              buttonLink="configure"
            />
          </CTASection>
        </Box>
        <CTASection
          title="Get assets on Palm"
          desc="DAI is a cryptocurrency pegged to the US Dollar, and is the most common token used to pay
          for NFTs and services on Palm. You can bring your existing DAI tokens from the Ethereum network to Palm using
          the token bridge. The bridge lets you bring other ERC-20 tokens to Palm, including Wrapped ETH (WETH)
          and Portion (PRT). When using the bridge, you will receive PALM tokens which are used to pay
          for gas on Palm network."
        >
          <CTACard
            title="Token Bridge"
            desc="Bridge DAI and other ERC-20 tokens between Ethereum and Palm."
            badge={false}
            badgeCopy="PALM provided"
            buttonCopy="Get Started"
            buttonLink="bridge"
          />
          {nftBridgeEnabled && (
            <CTACard
              title="NFT Bridge"
              desc="Transfer NFTs between Ethereum and Palm."
              badge={false}
              badgeCopy="PALM provided"
              buttonCopy="Get Started"
              buttonLink="nftbridge"
            />
          )}
        </CTASection>
      </VStack>
    </>
  );
};
export default HomePage;

interface ICTACard {
  title?: string;
  desc?: string;
  badge?: boolean;
  badgeCopy?: string;
  buttonCopy?: string;
  buttonLink?: string;
}

const CTACard: React.FC<ICTACard> = ({
  title,
  desc,
  badge,
  badgeCopy,
  buttonCopy,
  buttonLink,
}: ICTACard) => {
  const navigate = useNavigate();
  return (
    <Box
      w="352px"
      bg="palmNetwork.200"
      border="1px solid"
      borderColor="palmNetwork.100"
      color="palmNetwork.100"
      display="flex"
      flexWrap="wrap"
      p="8"
      mr="24px"
    >
      <Box mb="24px">
        <Heading as="h3" fontSize="24px" mr="16px" mb="24px">
          {title}
        </Heading>
        {badge && (
          <Badge
            borderRadius="0px"
            bg="#8142D5"
            color="white"
            textTransform="none"
          >
            {badgeCopy}
          </Badge>
        )}
        <Text fontSize="16px" mb="32px">
          {desc}
        </Text>
      </Box>

      <Box alignSelf="flex-end">
        <Button onClick={() => navigate(`/${buttonLink}`)}>{buttonCopy}</Button>
      </Box>
    </Box>
  );
};

interface ICTASection {
  title?: string;
  desc?: string;
  children: ReactNode | ReactNode[];
}

const CTASection: React.FC<ICTASection> = ({
  title,
  desc,
  children,
}: ICTASection) => {
  return (
    <Box pb="48px" pt="48px" maxW="728px" pl="16px">
      <Heading fontSize="24px" mb="16px">
        {title}
      </Heading>
      <Text mb="32px">{desc}</Text>
      <Box display="flex" justifyContent="flex-start">
        {children}
      </Box>
    </Box>
  );
};
